import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import { SettingsIcon } from "@chakra-ui/icons";

const Timer = ({ logData = [], isOn }) => {
  const [elapsed, setElapsed] = useState(0);
  const [shiftStartHour, setShiftStartHour] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Load shift start from localStorage
  useEffect(() => {
    const savedShift = localStorage.getItem("shiftStartHour");
    if (savedShift) {
      setShiftStartHour(Number(savedShift));
    } else {
      onOpen(); // open modal if not set
    }
  }, []);

  const saveShift = (hour) => {
    localStorage.setItem("shiftStartHour", hour);
    setShiftStartHour(Number(hour));
    onClose();
  };

  const getShiftBounds = (now = new Date()) => {
    if (shiftStartHour === null) return null;

    const cstNow = new Date(
      now.toLocaleString("en-US", { timeZone: "America/Chicago" })
    );

    let shiftStart = new Date(cstNow);
    shiftStart.setHours(shiftStartHour, 0, 0, 0);

    // shift always ends next day at the same hour (24h window)
    let shiftEnd = new Date(shiftStart);
    shiftEnd.setDate(shiftEnd.getDate() + 1);

    // If current time is before shiftStart, then shift belongs to previous day
    if (cstNow < shiftStart) {
      shiftStart.setDate(shiftStart.getDate() - 1);
      shiftEnd.setDate(shiftEnd.getDate() - 1);
    }

    return {
      shiftStart: shiftStart.getTime(),
      shiftEnd: shiftEnd.getTime(),
      now: cstNow.getTime(),
    };
  };

  const calcShiftDuration = () => {
    if (!shiftStartHour || !logData || logData.length === 0) return 0;

    const bounds = getShiftBounds();
    if (!bounds) return 0;
    const { shiftStart, shiftEnd, now } = bounds;

    let total = 0;
    for (const log of logData) {
      if (!log?.on) continue;
      const on = new Date(log.on).getTime();
      const off = log.off ? new Date(log.off).getTime() : now;

      const overlapStart = Math.max(on, shiftStart);
      const overlapEnd = Math.min(off, now, shiftEnd);
      if (overlapEnd > overlapStart) total += overlapEnd - overlapStart;
    }
    return total;
  };

  useEffect(() => {
    const update = () => setElapsed(calcShiftDuration());
    update();

    let id;
    if (isOn) id = setInterval(update, 1000);
    return () => id && clearInterval(id);
  }, [isOn, JSON.stringify(logData), shiftStartHour]);

  const fmt = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = String(Math.floor(s / 3600)).padStart(2, "0");
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };

  return (
    <>
      {/* Shift Timer Display */}
      {shiftStartHour !== null && (
        <Box
          m={10}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          mt={10}
        >
          <Heading
            as="h3"
            fontSize="15px"
            fontWeight="semibold"
            mb={4}
            color="gray.700"
            letterSpacing="wide"
          >
            Today IA Duration
            <SettingsIcon
              ml={2}
              boxSize={4}
              cursor="pointer"
              color="blue.500"
              onClick={onOpen} // Reopen modal on click
              _hover={{ color: "blue.700" }}
            />
          </Heading>

          <Text
            fontSize="6xl"
            fontWeight="bold"
            bgGradient="linear(to-r, orange.300, pink.400)"
            bgClip="text"
            textShadow="0px 3px 8px rgba(0,0,0,0.2)"
            px={10}
            py={4}
            borderRadius="2xl"
            boxShadow="lg"
            animation="pulse 2s infinite"
          >
            ⏱ {fmt(elapsed)}
          </Text>
        </Box>
      )}

      {/* Shift Start Modal */}
      <Modal isOpen={isOpen} onClose={() => {}} isCentered>
        <ModalOverlay backdropFilter="blur(6px)" />
        <ModalContent>
          <ModalHeader>Welcome To Intelli Assign Tracker</ModalHeader>
          <ModalBody>
            <Text mb={2}>Select the hour your shift starts in CST:</Text>
            <Select
              value={shiftStartHour}
              onChange={(e) => setShiftStartHour(Number(e.target.value))}
              placeholder="Select hour"
            >
              {Array.from({ length: 24 }).map((_, i) => (
                <option key={i} value={i}>
                  {i}:00
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="purple"
              onClick={() => saveShift(shiftStartHour)}
              isDisabled={shiftStartHour === null}
            >
              SAVE
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Timer;
